import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import type { Playlist, PlaylistItem } from '@jwp/ott-common/types/playlist';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { filterPlaylist, getFiltersFromConfig } from '@jwp/ott-common/src/utils/collection';
import { mediaURL } from '@jwp/ott-common/src/utils/urlFormatting';
import { useGADeviceType } from '@jwp/ott-common/src/utils/GADeviceType';
import ReactGA from 'react-ga4';
import _ from 'lodash';

import type { ScreenComponent } from '../../../../../types/screens';
import CardGrid from '../../../../components/CardGrid/CardGrid';
import Filter from '../../../../components/Filter/Filter';

import styles from './PlaylistGrid.module.scss';
import { useLocation, useNavigate, useParams } from 'react-router';
import { PLAYLIST_LIMIT } from '../../../../../../../packages/common/src/constants';
import ApiService from '@jwp/ott-common/src/services/ApiService';
import { getModule } from '@jwp/ott-common/src/modules/container';
import Button from '../../../../../../../packages/ui-react/src/components/Button/Button';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import { useTranslation } from 'react-i18next';
import { modalURLFromLocation } from '../../../../../../../packages/ui-react/src/utils/location';
import axios from 'axios';
import { useUIControls } from '@jwp/ott-hooks-react/src/useUIControls';
import { useContentStateStore } from '@jwp/ott-common/src/stores/ContentStateStore';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';

const PlaylistGrid: ScreenComponent<Playlist> = ({ data, isLoading }) => {
  const { config, accessModel } = useConfigStore(({ config, accessModel }) => ({ config, accessModel }), shallow);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const recentClick = useContentStateStore((s) => s.click);

  const breakpoint = useBreakpoint();
  const apiService = getModule(ApiService);
  const location = useLocation();
  const { singleClickPlayEnabled } = useUIControls();
  const { isDRMEnabled } = useAppConfig();

  const { id: pageId } = useParams<{ id: string }>();
  const [filter, setFilter] = useState<string>('');
  const [count, setCount] = useState<number>(PLAYLIST_LIMIT);
  const [updatedPlaylist, setUpdatedPlaylist] = useState<any>([]);
  const [isLive, setIsLive] = useState<boolean>(false);
  const categories = getFiltersFromConfig(config, data.feedid);
  const filteredPlaylist = useMemo(() => filterPlaylist(data, filter), [data, filter]);
  const [showLoadMore, setShowLoadMore] = useState(true);
  const shouldShowFilter = Boolean(categories.length);
  const isDRMMediaId = config?.menu.filter((item) => item?.contentId === recentClick?.contentId)?.[0];

  // User
  const { user, subscription } = useAccountStore(({ user, subscription }) => ({ user, subscription }), shallow);

  const { isAnalyticsEnable } = useAppConfig();
  const { click } = useContentStateStore.getState();
  const isLoggedIn = !!user;
  const userId = isLoggedIn ? user?.id : 'Guest user';
  const deviceType = useGADeviceType();
  const labelRef = useRef<string | undefined>();

  useEffect(() => {
    // reset filter when the playlist id changes
    setFilter('');
  }, [data.feedid]);

  useEffect(() => {
    setUpdatedPlaylist(data);
    setCount(PLAYLIST_LIMIT); // Reset count as well
  }, [data]);

  const pageTitle = `${data.title} - ${config.siteName}`;

  const getUrl = (playlistItem: PlaylistItem) => {
    const isSeriesContent = playlistItem.contentType == 'series';
    return mediaURL({ id: playlistItem.mediaid, title: playlistItem.title, playlistId: playlistItem.feedid, play: !isSeriesContent && singleClickPlayEnabled });
  };

  // const getUrl = (playlistItem: PlaylistItem) => mediaURL({ media: playlistItem, playlistId: playlistItem.feedid });
  const getDRMUrl = (playlistItem: PlaylistItem) =>
    mediaURL({ id: playlistItem.mediaid, title: playlistItem.title, playlistId: playlistItem.feedid, play: true });

  const pageState = useContentStateStore((s) => s.pages[pageId]);
  const setPageState = useContentStateStore((s) => s.setPageState);

  // Restore scroll
  const restoredRef = useRef(false);
  useEffect(() => {
    if (!restoredRef.current && pageState?.scrollPosition) {
      const tryRestore = () => {
        if (document.body.scrollHeight > pageState.scrollPosition) {
          window.scrollTo({
            top: pageState.scrollPosition + 200,
          });
          restoredRef.current = true;
        } else {
          requestAnimationFrame(tryRestore);
        }
      };
      tryRestore();
    }

    const handleScroll = () => {
      setPageState(pageId, { scrollPosition: window.scrollY, from: 'pageId' });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [pageId, pageState?.scrollPosition, setPageState]);

  const handleLoadMoreClick = async () => {
    //Increase count for each click
    const contentStore = useContentStateStore.getState();
    const pageState = contentStore.pages[pageId];

    //playlist length to find the end of pagination
    const playListLength = (updatedPlaylist?.playlist?.length ? updatedPlaylist?.playlist?.length : filteredPlaylist?.playlist?.length) ?? 0;

    if (!pageState.hasNext) {
      setShowLoadMore(false);
      return;
    }

    // Compute next offset and limit from store
    const nextPageOffset = (pageState?.pageOffset ?? 0) + PLAYLIST_LIMIT;
    const nextPageLimit = PLAYLIST_LIMIT;

    let combinedPlayList;
    const fetchedPlaylist = await apiService.getPlaylistById(filteredPlaylist?.feedid, {
      page_limit: PLAYLIST_LIMIT.toString(),
      page_offset: nextPageOffset.toString(),
    });

    if (fetchedPlaylist) {
      if (updatedPlaylist?.playlist?.length) {
        combinedPlayList = updatedPlaylist?.playlist.concat(fetchedPlaylist?.playlist);
      } else {
        combinedPlayList = filteredPlaylist?.playlist.concat(fetchedPlaylist?.playlist);
      }

      if (combinedPlayList) {
        fetchedPlaylist.playlist = combinedPlayList;
        setUpdatedPlaylist(fetchedPlaylist);
      }
      contentStore.setPageState(pageId, {
        pageOffset: nextPageOffset,
        pageLimit: nextPageLimit,
        totalLoaded: fetchedPlaylist?.playlist.length,
        hasNext: Boolean(fetchedPlaylist?.links?.next),
        lastFetched: Date.now(),
      });
    }
  };

  const handleGetDrmContent = async (playlistItem: any) => {
    const { data: drmData } = await axios({
      method: 'get',
      url: `https://cdn.jwplayer.com/v2/media/${isDRMMediaId?.custom?.drmMediaId}`,
    });
    playlistItem.title = drmData.title;
    playlistItem.mediaid = isDRMMediaId?.custom?.drmMediaId;
    navigate(getDRMUrl(playlistItem));
  };

  const handleMedia = async (playlistItem: any) => {
    if (filteredPlaylist?.isLiveChannel) {
      const { data: drmData } = await axios({
        method: 'get',
        url: `https://cdn.jwplayer.com/v2/media/${playlistItem?.mediaid}`,
      });
      setIsLive(true);
      playlistItem.title = drmData.title;
      navigate(getDRMUrl(playlistItem));
    } else {
      setIsLive(false);
    }
  };

  useEffect(() => {
    if (filteredPlaylist?.playlist?.length < PLAYLIST_LIMIT) {
      setShowLoadMore(false);
    }
    if (isDRMMediaId?.custom?.drmMediaId) {
      handleGetDrmContent(isDRMMediaId);
    } else {
      filteredPlaylist?.playlist.map((playlistItem: PlaylistItem) => {
        handleMedia(playlistItem);
      });
    }
  }, [filteredPlaylist, user, isLive, handleGetDrmContent, isDRMMediaId]);

  useEffect(() => {
    return () => {
      if (!isDRMMediaId) {
        useContentStateStore.setState({
          click: {
            label: '',
            contentId: '',
          },
        });
      }
    };
  }, []);

  useEffect(() => {
    if (!isAnalyticsEnable) {
      return;
    }
    const pathname = location.pathname;
    const segments = pathname.split('/');
    const contentIdFromPath = segments[2];
    const activeItem = config?.menu?.find((item) => item.contentId === contentIdFromPath);
    if (pathname === '/') {
      labelRef.current = 'Home';
    } else if (click?.label) {
      labelRef.current = click.label;
    } else if (activeItem?.label) {
      labelRef.current = activeItem?.label;
    }
  }, [data.playlist.length, location.pathname, config.menu, click]);

  useEffect(() => {
    if (!isAnalyticsEnable) {
      return;
    }

    let lastScrollTop = 0;

    const handleScroll = _.throttle(() => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const direction = scrollTop > lastScrollTop ? 'Down' : 'Up';
      lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;

      if (scrollTop > 0) {
        sendGAEvent('scroll', {
          scroll_direction: direction,
          userid_no: userId,
          device_type: deviceType,
        });
      }
    }, 1000);

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className={styles.playlist}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta property="og:title" content={pageTitle} />
        <meta name="twitter:title" content={pageTitle} />
      </Helmet>
      <div className={`${styles.main} ${isLive && styles.hideMain}`}>
        <header className={styles.header}>
          <h1>{data.title}</h1>
          {shouldShowFilter && <Filter name="genre" value={filter} defaultLabel="All" options={categories} setValue={setFilter} />}
        </header>
        <CardGrid
          getUrl={getUrl}
          playlist={updatedPlaylist?.playlist ? updatedPlaylist : filteredPlaylist}
          accessModel={accessModel}
          isLoggedIn={!!user}
          hasSubscription={!!subscription}
          isLoading={isLoading}
          headingLevel={2}
        />
        {showLoadMore && updatedPlaylist?.links?.next && (
          <div className={styles.loadMoreBtn}>
            <Button
              label={t('common:load_more')}
              aria-label={t('loadMore')}
              onClick={handleLoadMoreClick}
              color={'default'}
              fullWidth={breakpoint < Breakpoint.md}
              size="large"
              type="submit"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PlaylistGrid;
